import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

/**
 * Promise-based confirm dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Remove this experience?',
 *     description: 'This entry will be removed from your profile.',
 *     confirmText: 'Remove',
 *     destructive: true,
 *   });
 *   if (ok) doRemove();
 *
 * Replaces `window.confirm` — looks native to the app, supports rich text, and
 * blocks the awaiter without freezing the UI.
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const busyRef = useRef(false);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const close = (value: boolean) => {
    if (busyRef.current) return;
    busyRef.current = true;
    pending?.resolve(value);
    setPending(null);
    // Reset on next tick so back-to-back confirms don't get swallowed.
    queueMicrotask(() => {
      busyRef.current = false;
    });
  };

  const opts = pending?.options;
  const destructive = opts?.destructive ?? false;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={pending !== null} onOpenChange={(open) => !open && close(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              {destructive ? (
                <span className="mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-full bg-red-500/10 text-red-600 dark:text-red-300">
                  <AlertTriangle className="h-4 w-4" />
                </span>
              ) : null}
              <div className="space-y-1.5">
                <DialogTitle>{opts?.title}</DialogTitle>
                {opts?.description ? (
                  <DialogDescription>{opts.description}</DialogDescription>
                ) : null}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => close(false)}>
              {opts?.cancelText ?? 'Cancel'}
            </Button>
            <Button
              variant={destructive ? 'destructive' : 'default'}
              onClick={() => close(true)}
              autoFocus
            >
              {opts?.confirmText ?? 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmDialogProvider>');
  }
  return ctx;
}
